import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { server } from "../../server";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import Right from "./Right";
import { fetchActivityData } from "../../redux/activitySlice";
import { getUserIdFromToken } from "../../utils/auth";

function Activity() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState("sent");
  const [visitedProfiles, setVisitedProfiles] = useState(0);
  const [shortlisted, setShortlisted] = useState(0);
  const [requestedPhotoCount, setRequestedPhotoCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const activityData = useSelector((state) => state.activity);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const userId = await getUserIdFromToken();

        const [visitsResponse, shortlistResponse, requestedPhotoResponse] =
          await Promise.all([
            fetch(
              `${server}/profilevisit/GetProfileVisitByUserId?userId=${userId}`,
              { headers: { Authorization: token } }
            ),
            fetch(
              `${server}/shortlist/GetShortlistsByUserId?userId=${userId}`,
              { headers: { Authorization: token } }
            ),
            fetch(
              `${server}/photoRequest/GetPhotoRequestsByUserId?userId=${userId}`,
              { headers: { Authorization: token } }
            ),
          ]);

        const visitsData = await visitsResponse.json();
        const shortlistData = await shortlistResponse.json();
        const requestedPhotoData = await requestedPhotoResponse.json();

        setVisitedProfiles(visitsData.visits?.length || 0);
        setShortlisted(shortlistData.shortlists?.length || 0);
        setRequestedPhotoCount(requestedPhotoData?.length || 0);
      } catch (err) {
        console.error("Failed to fetch profiles:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const loadActivity = useCallback(async () => {
    const uid = await getUserIdFromToken();
    if (uid) dispatch(fetchActivityData(uid));
  }, [dispatch]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const tabCounts = {
    received: activityData.receivedRequests?.length || 0,
    sent: activityData.sentRequests?.length || 0,
    accepted: activityData.acceptedRequests?.length || 0,
  };

  const getOtherProfileId = (item) => {
    if (selectedTab === "sent") return item.receiverId;
    if (selectedTab === "received") return item.senderId;
    if (selectedTab === "accepted") return item._id;
    return null;
  };

  const currentList = (
    selectedTab === "received"
      ? activityData.receivedRequests
      : selectedTab === "accepted"
      ? activityData.acceptedRequests
      : activityData.sentRequests
  )
    ?.slice()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <>
      <Header />

      <section>
        <div className="container pt-3">
          <div className="row g-3">
            <div className="col-lg-8 col-12 mb-5">
              <div className="row g-2">
                <div className="col-lg-4 col-6">
                  <div
                    className="px-4 py-3 bg-white shadow rounded-4 d-flex align-items-center cursor-pointer h-100"
                    onClick={() => navigate("/profile-visited")}
                  >
                    <img
                      src="/assets/images/profile-visited.png"
                      width="40"
                      alt=""
                    />
                    <div className="ms-3">
                      <p className="fs-4 m-0 lh-1 text-dark">Profile Visited</p>
                      <p className="lh-1 mt-1 m-0">{visitedProfiles} Profiles</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4 col-6">
                  <div
                    className="px-4 py-3 bg-white shadow rounded-4 d-flex align-items-center cursor-pointer h-100"
                    onClick={() => navigate("/shortlisted")}
                  >
                    <img src="/assets/images/star.png" width="40" alt="" />
                    <div className="ms-3">
                      <p className="fs-4 m-0 lh-1 text-dark">Shortlisted</p>
                      <p className="lh-1 mt-1 m-0">{shortlisted} Profiles</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4 col-6">
                  <div
                    className="px-4 py-3 bg-white shadow rounded-4 d-flex align-items-center cursor-pointer h-100"
                    onClick={() => navigate("/requested-photo")}
                  >
                    <img
                      src="/assets/images/requested-photo.png"
                      width="40"
                      alt=""
                    />
                    <div className="ms-3">
                      <p className="fs-4 m-0 lh-1 text-dark">Requested Photo</p>
                      <p className="lh-1 mt-1 m-0">{requestedPhotoCount} Profiles</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <h3 className="fw-medium ls-sm text-dark lh-1 fs-4 m-0">
                  My Activity &mdash; Inbox
                </h3>
                <p className="lh-sm fw-normal m-0">
                  Track your profile visits, requests sent & received, and
                  accepted matches.
                </p>
              </div>

              <div className="mt-3 d-flex gap-1">
                {["sent", "received", "accepted"].map((tab) => (
                  <span
                    key={tab}
                    className={`py-1 rounded-pill fw-normal px-4 cursor-pointer ${
                      selectedTab === tab
                        ? "bg-red-primary text-white"
                        : "border bg-white"
                    }`}
                    onClick={() => setSelectedTab(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)} (
                    {tabCounts[tab]})
                  </span>
                ))}
              </div>

              <div className="pt-4">
                {currentList?.length > 0 ? (
                  <div className="row g-2">
                    {currentList.map((item) => (
                      <div className="col-lg-4 col-md-6 col-12" key={item._id}>
                        <div
                          className="position-relative rounded-4 d-flex align-items-end"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            navigate("/Profile", {
                              state: { profileId: getOtherProfileId(item) },
                            })
                          }
                        >
                          <img
                            src={
                              item?.profileDetails?.images?.[0]?.docUrl ||
                              "/assets/images/default-profile.jpg"
                            }
                            className="w-100 object-fit-cover rounded-4"
                            height="380"
                            alt=""
                          />
                          <div className="position-absolute p-3 dark-gradient w-100 rounded-4 d-flex justify-content-between flex-column h-100">
                            <div className="d-flex justify-content-between align-items-center">
                              <p className="text-white m-0 lh-sm fs-6">
                                Request {selectedTab}
                                <br />
                                on{" "}
                                {new Date(item.createdAt).toLocaleDateString()}
                              </p>
                              {item?.profileDetails?.images?.length > 0 && (
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate("/images", {
                                      state: { profileId: item?.profileDetails?._id },
                                    });
                                  }}
                                  className="icon-shape icon-sm bg-white rounded-circle"
                                >
                                  <i className="bi bi-image fs-4"></i>
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="text-uppercase text-white m-0 lh-1 fs-6 fw-semibold">
                                GR{item?.profileDetails?.userId}
                              </p>
                              <h4 className="fw-normal ls-sm fw-medium text-white lh-1 mt-1 mb-0">
                                {item?.profileDetails?.name},{" "}
                                {Math.floor(
                                  (Date.now() -
                                    item?.profileDetails?.profile?.dob) /
                                    3.15576e10
                                )}{" "}
                                <img
                                  src="https://cdn-icons-png.flaticon.com/512/8207/8207939.png"
                                  width="20"
                                  className="ms-1"
                                  alt=""
                                />
                              </h4>
                              <p className="d-flex align-items-center m-0 lh-sm mt-2 text-white fs-6">
                                {item?.profileDetails?.profile?.height}{" "}
                                <i className="bi bi-circle-fill fs-dot mx-2"></i>{" "}
                                {item?.profileDetails?.profile?.community}
                              </p>
                              <p className="d-flex align-items-center m-0 lh-sm text-white fs-6">
                                {item?.profileDetails?.profile?.profession ||
                                  "No Profession"}{" "}
                                <i className="bi bi-circle-fill fs-dot mx-2"></i>{" "}
                                {item?.profileDetails?.profile?.annualIncome ||
                                  "No Income"}
                              </p>
                              <p className="d-flex align-items-center m-0 lh-sm text-white fs-6">
                                {item?.profileDetails?.profile?.religion}{" "}
                                <i className="bi bi-circle-fill fs-dot mx-2"></i>{" "}
                                {item?.profileDetails?.profile?.maritalStatus}
                              </p>
                              <div className="bg-light-gradient-right ms-n4 p-1 mt-4 mb-2">
                                <p className="text-dark m-0 lh-sm fw-normal ms-3 fs-6 fst-italic">
                                  Profile Managed by{" "}
                                  {item?.profileDetails?.profileFor}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center w-100">
                    <img
                      src="/assets/images/no-data-2.png"
                      width="150"
                      alt=""
                    />
                  </div>
                )}
              </div>
            </div>

            <Right />
          </div>
        </div>
      </section>
    </>
  );
}

export default Activity;
